/**
 * The BHCS school calendar, as an editable month grid.
 *
 * This page is the source of truth for which days programs do not run. It used to be a
 * hand-maintained TypeScript file that only the React bundle could read, which meant the
 * query agent could not answer "how many sessions does Chess have" and nobody could correct
 * a date without a deploy.
 *
 * The three kinds are the whole point of the screen, so they are named and explained rather
 * than reduced to a colour: a day that stops after-school is a completely different fact from
 * a day that stops enrichment, and picking the wrong one either cancels classes that are
 * running or bills families for days they cannot attend.
 */
import React, { useMemo, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { CalendarOff, ChevronLeft, ChevronRight, Trash2 } from 'lucide-react';
import { AdminLayout } from '../AdminLayout';
import {
  fetchClosures,
  upsertClosure,
  deleteClosure,
  fetchProgramExcludedDates,
  type ProgramExcludedDates,
} from '../../services/closures';
import { primeClosures, type BlackoutDate, type BlackoutKind } from '../../config/blackoutDates';
import { BRAND_KEYS, DEFAULT_BRAND_KEY, type BrandKey } from '../../config/brands';
import { BRAND_PROFILES } from '../../config/brandProfiles';
import { getTodayDateStringInBusinessTZ } from '../../utils/date';

/**
 * Menu order, and the order the legend reads.
 *
 * Two kinds, not three. A `no_afterschool` kind existed briefly for camp weeks, on the
 * theory that enrichment carried on without after-school — but enrichment classes meet
 * inside the after-school block, so that is not a state that can occur, and no class ever
 * met on such a day.
 */
const KINDS: { id: BlackoutKind; label: (brand: BrandKey) => string; blurb: string; chip: string }[] = [
  {
    id: 'closed',
    // Named for the brand whose calendar is open: "BHCS Closed" on a Kaleido closure is
    // simply the wrong sentence, and this label is also what the parent-facing "No class on"
    // row echoes.
    label: (brand) => `${BRAND_PROFILES[brand].shortName} Closed`,
    blurb: 'Neither after-school nor enrichment runs — a shut building, or a camp week that replaces after-school.',
    chip: 'bg-red-100 text-red-800 border-red-200',
  },
  {
    id: 'no_enrichment',
    label: () => 'No enrichment',
    blurb: 'After-school runs normally and stays billable. Every enrichment class is cancelled.',
    chip: 'bg-sky-100 text-sky-800 border-sky-200',
  },
];

const KIND_BY_ID = new Map(KINDS.map(k => [k.id, k]));
const WEEKDAY_LABELS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

/** `2026-09` → the days of that month plus the leading blanks that align it to a week. */
function monthCells(month: string): Array<{ ymd: string; day: number } | null> {
  const [y, m] = month.split('-').map(Number);
  const total = new Date(Date.UTC(y, m, 0)).getUTCDate();
  const firstDow = new Date(Date.UTC(y, m - 1, 1)).getUTCDay();
  const cells: Array<{ ymd: string; day: number } | null> = Array(firstDow).fill(null);
  for (let day = 1; day <= total; day++) {
    cells.push({ day, ymd: `${month}-${String(day).padStart(2, '0')}` });
  }
  return cells;
}

function shiftMonth(month: string, delta: number): string {
  const [y, m] = month.split('-').map(Number);
  const d = new Date(Date.UTC(y, m - 1 + delta, 1));
  return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}`;
}

function monthTitle(month: string): string {
  const [y, m] = month.split('-').map(Number);
  return new Date(Date.UTC(y, m - 1, 1)).toLocaleDateString('en-US', {
    month: 'long', year: 'numeric', timeZone: 'UTC',
  });
}

const AdminClosures: React.FC = () => {
  const queryClient = useQueryClient();
  const [month, setMonth] = useState(() => getTodayDateStringInBusinessTZ().slice(0, 7));
  const [editing, setEditing] = useState<BlackoutDate | null>(null);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);

  /**
   * Which brand's calendar is being edited. One screen, one calendar at a time.
   *
   * The calendars are independent, not variations on each other: a Kaleido closure is not a
   * non-billable BHCS day, and the reverse. Showing them merged would render a Kaleido
   * closure as a BHCS day, and the Delete beside it would take a day off a calendar this
   * screen never showed.
   */
  const [brand, setBrand] = useState<BrandKey>(DEFAULT_BRAND_KEY);

  // `fetchClosures` returns every brand's rows so the shared query can prime all the
  // calendars at once; the narrowing happens here rather than in the fetch.
  const { data: allBrandClosures = [], isLoading } = useQuery<BlackoutDate[]>({
    queryKey: ['program-closures'],
    queryFn: fetchClosures,
  });
  const closures = useMemo(
    () => allBrandClosures.filter((c) => (c.brand_key ?? DEFAULT_BRAND_KEY) === brand),
    [allBrandClosures, brand],
  );

  // Shown, never edited. A program's own days belong to the program; surfacing them here
  // answers "what is off in October" without splitting ownership across two screens.
  const { data: programDates = [] } = useQuery<ProgramExcludedDates[]>({
    queryKey: ['program-excluded-dates'],
    queryFn: fetchProgramExcludedDates,
  });

  const byDate = useMemo(() => new Map(closures.map(c => [c.date, c])), [closures]);

  /** date → the programs that skip it on their own, for the grid marker and the list. */
  const programsByDate = useMemo(() => {
    const m = new Map<string, string[]>();
    for (const p of programDates) {
      for (const d of p.excluded_dates) {
        const names = m.get(d);
        if (names) names.push(p.name);
        else m.set(d, [p.name]);
      }
    }
    return m;
  }, [programDates]);
  const cells = useMemo(() => monthCells(month), [month]);
  const inMonth = useMemo(
    () => closures.filter(c => c.date.startsWith(month)).sort((a, b) => a.date.localeCompare(b.date)),
    [closures, month],
  );

  const programInMonth = useMemo(
    () =>
      [...programsByDate.entries()]
        .filter(([d]) => d.startsWith(month))
        .sort(([a], [b]) => a.localeCompare(b)),
    [programsByDate, month],
  );

  /**
   * Re-read after every write, and re-prime the in-memory calendar from the result.
   *
   * Without the re-prime an admin would edit a day here and keep seeing the old answer
   * everywhere else in their own session until a reload — the accessors read a module-level
   * cache, not this query.
   */
  async function refresh() {
    const rows = await queryClient.fetchQuery({ queryKey: ['program-closures'], queryFn: fetchClosures });
    primeClosures(rows);
  }

  async function save(row: BlackoutDate) {
    setBusy(true);
    setMsg(null);
    try {
      await upsertClosure({ ...row, brand_key: brand });
      await refresh();
      setEditing(null);
      setMsg(`Saved ${row.date}.`);
    } catch (e) {
      setMsg(e instanceof Error ? e.message : 'Could not save that closure');
    } finally {
      setBusy(false);
    }
  }

  async function remove(date: string) {
    setBusy(true);
    setMsg(null);
    try {
      // Load-bearing: without the brand this deletes every brand's row for that date, so
      // reopening a BHCS day would silently reopen the same day for Kaleido.
      await deleteClosure(date, brand);
      await refresh();
      setEditing(null);
      setMsg(`${date} is now an ordinary running day.`);
    } catch (e) {
      setMsg(e instanceof Error ? e.message : 'Could not remove that closure');
    } finally {
      setBusy(false);
    }
  }

  return (
    <AdminLayout>
      {/*
        On desktop AdminLayout is `h-screen overflow-hidden` and <main> does not scroll, so a
        page taller than the viewport is simply clipped — every admin page owns its own scroll
        region. The heading stays put and the calendar below it scrolls.
      */}
      <div className="lg:flex lg:flex-col lg:flex-1 lg:min-h-0">
      <div className="mb-6 flex-shrink-0">
        <h1 className="text-2xl font-bold text-gray-900">School Calendar</h1>
        <p className="text-sm text-gray-500 mt-1">
          Days programs do not run, school-wide. A single vendor skipping its own day belongs on
          that program instead, not here.
        </p>
      </div>

      {/*
        Capped rather than fluid. A month grid stretched across an ultrawide window turns each
        day into a ~265px square and pushes half the month below the fold; the content is seven
        columns of a two-digit number, so it stops growing well before the window does.
      */}
      <div className="max-w-4xl lg:flex-1 lg:min-h-0 lg:overflow-y-auto lg:pr-2">

      <div className="grid gap-2 sm:grid-cols-3 mb-6">
        {KINDS.map(k => (
          <div key={k.id} className={`rounded-lg border px-3 py-2 ${k.chip}`}>
            <div className="text-sm font-semibold">{k.label(brand)}</div>
            <div className="text-xs mt-0.5 opacity-80">{k.blurb}</div>
          </div>
        ))}
        <div className="rounded-lg border border-dashed border-gray-400 bg-white px-3 py-2 text-gray-700">
          <div className="text-sm font-semibold">One class only</div>
          <div className="text-xs mt-0.5 text-gray-500">
            A program&rsquo;s own day off. Shown here, edited in Admin &rarr; Programs.
          </div>
        </div>
      </div>

      {closures.length === 1 && (
        <div className="mb-4 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900">
          Only one closure left. The calendar cannot be emptied completely — an empty result is
          treated as a failed load and the built-in calendar is used instead.
        </div>
      )}

      {/* Which calendar is being edited. Above the grid, not beside the month, because it
          changes what every cell below means -- a reader who misses it edits the wrong
          brand's year. */}
      <div className="bg-white rounded-xl border border-gray-200 p-4 mb-4">
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-sm font-medium text-gray-700">Editing calendar:</span>
          <div className="inline-flex rounded-lg border border-gray-300 p-0.5">
            {BRAND_KEYS.map((key) => {
              const profile = BRAND_PROFILES[key];
              const isActive = key === brand;
              return (
                <button
                  key={key}
                  type="button"
                  aria-pressed={isActive}
                  onClick={() => setBrand(key)}
                  className="flex items-center rounded-md px-3 py-1.5 text-sm font-medium transition-colors"
                  style={isActive ? { backgroundColor: profile.accent, color: '#fff' } : { color: '#4b5563' }}
                >
                  {profile.shortName}
                </button>
              );
            })}
          </div>
          <p className="w-full text-xs text-gray-500">
            The two calendars are independent — a closure here affects {BRAND_PROFILES[brand].shortName} only.
          </p>
        </div>
      </div>

      <div className="bg-white rounded-xl border border-gray-200 p-4">
        <div className="flex items-center justify-between mb-4">
          <button
            onClick={() => setMonth(shiftMonth(month, -1))}
            className="p-2 rounded-lg hover:bg-gray-100"
            aria-label="Previous month"
          >
            <ChevronLeft className="h-5 w-5" />
          </button>
          <div className="font-semibold text-gray-900">{monthTitle(month)}</div>
          <button
            onClick={() => setMonth(shiftMonth(month, 1))}
            className="p-2 rounded-lg hover:bg-gray-100"
            aria-label="Next month"
          >
            <ChevronRight className="h-5 w-5" />
          </button>
        </div>

        <div className="grid grid-cols-7 gap-1 text-center text-xs text-gray-500 mb-1">
          {WEEKDAY_LABELS.map(d => <div key={d}>{d}</div>)}
        </div>

        <div className="grid grid-cols-7 gap-1">
          {cells.map((cell, i) => {
            if (!cell) return <div key={`blank-${i}`} />;
            const closure = byDate.get(cell.ymd);
            const kind = closure ? KIND_BY_ID.get(closure.kind) : undefined;
            const programs = programsByDate.get(cell.ymd);
            const title = closure
              ? `${closure.label} (${kind?.label(brand)})`
              : programs
                ? `${programs.join(', ')} — program's own day off. Edit in Admin → Programs.`
                : 'Mark as a closure';
            return (
              <button
                key={cell.ymd}
                onClick={() =>
                  setEditing(closure ?? { date: cell.ymd, label: '', kind: 'closed', brand_key: brand })
                }
                title={title}
                className={`h-14 sm:h-16 rounded-lg border text-sm flex flex-col items-center justify-center gap-0.5 transition
                  ${kind
                    ? kind.chip
                    : programs
                      // Dashed, not filled: the school is open, one class is not.
                      ? 'bg-white text-gray-700 border-dashed border-gray-400 hover:border-gray-600'
                      : 'bg-white text-gray-700 border-gray-200 hover:border-gray-400'}`}
              >
                <span className="font-medium">{cell.day}</span>
                {closure ? (
                  <CalendarOff className="h-3 w-3 opacity-70" />
                ) : programs ? (
                  <span className="text-[10px] leading-none text-gray-500">
                    {programs.length} class{programs.length === 1 ? '' : 'es'}
                  </span>
                ) : null}
              </button>
            );
          })}
        </div>
      </div>

      {msg && <div className="mt-3 text-sm text-gray-700">{msg}</div>}

      <div className="mt-6">
        <h2 className="text-sm font-semibold text-gray-900 mb-2">
          {monthTitle(month)} — {inMonth.length} closure{inMonth.length === 1 ? '' : 's'}
        </h2>
        {isLoading ? (
          <div className="h-20 bg-gray-100 animate-pulse rounded-lg" />
        ) : inMonth.length === 0 ? (
          <p className="text-sm text-gray-500">Nothing closed this month.</p>
        ) : (
          <ul className="divide-y divide-gray-100 border border-gray-200 rounded-lg bg-white">
            {inMonth.map(c => (
              <li key={c.date} className="flex items-center justify-between px-3 py-2">
                <div className="min-w-0">
                  <div className="text-sm text-gray-900">
                    {c.date} — {c.label}
                  </div>
                  <div className="text-xs text-gray-500">{KIND_BY_ID.get(c.kind)?.label(brand)}</div>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setEditing(c)}
                    className="text-sm text-blue-600 hover:underline"
                  >
                    Edit
                  </button>
                  <button
                    onClick={() => remove(c.date)}
                    disabled={busy}
                    className="p-1.5 rounded hover:bg-red-50 text-red-600 disabled:opacity-50"
                    aria-label={`Remove closure on ${c.date}`}
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>

      </div>
      </div>

      {programInMonth.length > 0 && (
        <div className="mt-6">
          <h2 className="text-sm font-semibold text-gray-900 mb-2">
            {monthTitle(month)} — program-specific days
          </h2>
          <p className="text-xs text-gray-500 mb-2">
            Read-only here. These belong to the program, so they are edited in Admin &rarr;
            Programs; a day the whole school is closed belongs in the calendar above instead.
          </p>
          <ul className="divide-y divide-gray-100 border border-dashed border-gray-300 rounded-lg bg-white">
            {programInMonth.map(([date, names]) => (
              <li key={date} className="px-3 py-2">
                <div className="text-sm text-gray-900">{date}</div>
                <div className="text-xs text-gray-500">{names.join(' · ')}</div>
              </li>
            ))}
          </ul>
        </div>
      )}

      {editing && (
        <ClosureEditor
          value={editing}
          brand={brand}
          busy={busy}
          existing={byDate.has(editing.date)}
          onCancel={() => setEditing(null)}
          onSave={save}
          onRemove={remove}
        />
      )}
    </AdminLayout>
  );
};

interface EditorProps {
  value: BlackoutDate;
  /** Whose calendar this closure belongs to — names the "… Closed" kind. */
  brand: BrandKey;
  busy: boolean;
  existing: boolean;
  onCancel: () => void;
  onSave: (row: BlackoutDate) => void;
  onRemove: (date: string) => void;
}

const ClosureEditor: React.FC<EditorProps> = ({ value, brand, busy, existing, onCancel, onSave, onRemove }) => {
  const [label, setLabel] = useState(value.label);
  const [kind, setKind] = useState<BlackoutKind>(value.kind);

  return (
    <div className="fixed inset-0 bg-black/40 flex items-end sm:items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl w-full max-w-md p-5">
        <h3 className="font-semibold text-gray-900 mb-1">{value.date}</h3>
        <p className="text-xs text-gray-500 mb-4">
          {existing ? 'Editing an existing closure.' : 'Marking this day as a closure.'}
        </p>

        <label className="block text-sm font-medium text-gray-700 mb-1">Reason</label>
        <input
          value={label}
          onChange={e => setLabel(e.target.value)}
          placeholder="BHCS Closed — Memorial Day"
          className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm mb-4"
        />

        <div className="block text-sm font-medium text-gray-700 mb-2">What does it stop?</div>
        <div className="space-y-2 mb-5">
          {KINDS.map(k => (
            <label
              key={k.id}
              className={`flex gap-2 items-start border rounded-lg px-3 py-2 cursor-pointer
                ${kind === k.id ? 'border-blue-500 bg-blue-50' : 'border-gray-200'}`}
            >
              <input
                type="radio"
                name="kind"
                checked={kind === k.id}
                onChange={() => setKind(k.id)}
                className="mt-1"
              />
              <span>
                <span className="block text-sm font-medium text-gray-900">{k.label(brand)}</span>
                <span className="block text-xs text-gray-500">{k.blurb}</span>
              </span>
            </label>
          ))}
        </div>

        <div className="flex justify-between items-center">
          {existing ? (
            <button
              onClick={() => onRemove(value.date)}
              disabled={busy}
              className="text-sm text-red-600 hover:underline disabled:opacity-50"
            >
              Remove
            </button>
          ) : <span />}
          <div className="flex gap-2">
            <button onClick={onCancel} className="px-3 py-2 text-sm text-gray-600 hover:bg-gray-100 rounded-lg">
              Cancel
            </button>
            <button
              onClick={() => onSave({ date: value.date, label: label.trim(), kind, brand_key: brand })}
              disabled={busy || label.trim().length === 0}
              className="px-4 py-2 text-sm bg-blue-600 text-white rounded-lg disabled:opacity-50"
            >
              {busy ? 'Saving…' : 'Save'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminClosures;
